import { Button } from '@/components/ui/button'
import type { IntegrationMainViewProps } from '@/types/integration'
import { CheckCircle2, AlertCircle, Clock, TrendingUp } from 'lucide-react'

export const MockDashboard = ({ initialData, onClose }: IntegrationMainViewProps) => {
  const stats = [
    { label: 'Total Projects', value: '12', icon: CheckCircle2, color: 'text-green-500' },
    { label: 'Active Tasks', value: '47', icon: Clock, color: 'text-blue-500' },
    { label: 'Overdue', value: '3', icon: AlertCircle, color: 'text-red-500' },
    { label: 'Completed', value: '156', icon: TrendingUp, color: 'text-green-500' },
  ]

  const recentActivity = [
    { id: '1', action: 'Created', item: 'New project Alpha', time: '5m ago' },
    { id: '2', action: 'Updated', item: 'Design specifications', time: '1h ago' },
    { id: '3', action: 'Completed', item: 'Homepage redesign', time: '3h ago' },
    { id: '4', action: 'Commented', item: 'Client feedback review', time: '5h ago' },
  ]

  return (
    <div className="h-full overflow-y-auto bg-background">
      {/* Header */}
      <div className="border-b border-border bg-muted/30 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Mock Integration Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Testing the integration framework with sample data
            </p>
          </div>
          {onClose && (
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>

        {/* Initial Data Display (if passed from sidebar) */}
        {initialData && (
          <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-md">
            <p className="text-xs font-medium text-purple-700">
              Received data from sidebar:
            </p>
            <pre className="text-xs mt-1 text-purple-600">
              {JSON.stringify(initialData, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(stat => (
            <div key={stat.label} className="p-4 border border-border rounded-lg bg-background">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <stat.icon className={`h-8 w-8 ${stat.color}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Recent Activity */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
          <div className="space-y-2">
            {recentActivity.map(activity => (
              <div
                key={activity.id}
                className="p-3 bg-accent/50 rounded-md hover:bg-accent transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-md border border-border bg-background">
                        {activity.action}
                      </span>
                      <span className="text-sm font-medium">{activity.item}</span>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Showcase */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Integration Framework Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border border-border rounded-lg bg-background">
              <h3 className="font-medium mb-2">✅ Dynamic Sidebar Tab</h3>
              <p className="text-sm text-muted-foreground">
                This integration appears in the left sidebar when enabled
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-background">
              <h3 className="font-medium mb-2">✅ Main Panel View</h3>
              <p className="text-sm text-muted-foreground">
                Custom dashboard appears as a main panel tab
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-background">
              <h3 className="font-medium mb-2">✅ Type Safety</h3>
              <p className="text-sm text-muted-foreground">
                Full TypeScript support for integration components
              </p>
            </div>
            <div className="p-4 border border-border rounded-lg bg-background">
              <h3 className="font-medium mb-2">✅ Extensible</h3>
              <p className="text-sm text-muted-foreground">
                Easy to add new integrations following this pattern
              </p>
            </div>
          </div>
        </div>

        {/* Test Actions */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-4">Test Actions</h2>
          <div className="flex gap-2">
            <Button variant="default" size="sm">
              Primary Action
            </Button>
            <Button variant="outline" size="sm">
              Secondary Action
            </Button>
            <Button variant="ghost" size="sm">
              Tertiary Action
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

